import { colors } from '@ctw/config/zui';
import type { CdaSection } from '@ctw/shared/content/document-viewer/_internal/parsers/types';
import { uniqueIdentifierForNode } from '@ctw/shared/content/document-viewer/_internal/renderers/helpers';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { trim } from 'lodash-es';
import { styles } from './pdf-cda-renderer';
import { PdfNodeRenderer } from './pdf-node-renderer';

const tableStyles = StyleSheet.create({
  table: {
    width: '100%',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.background.subtle,
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 12,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    borderBottomWidth: 1,
    fontFamily: 'Inter',
    fontWeight: 400,
    borderBottomColor: colors.background.subtle,
  },
  header: {
    fontWeight: 600,
    letterSpacing: '0.05rem',
    fontFamily: 'Inter',
    fontSize: 8,
    color: colors.muted.text,
  },
  cell: {
    flex: 1,
    padding: 5,
    fontFamily: 'Inter',
    fontWeight: 400,
  },
  lastCell: {
    flex: 1,
    padding: 5,
    fontFamily: 'Inter',
    fontWeight: 400,
  },
});

interface PdfTableRendererProps {
  section: CdaSection;
  table: HTMLTableElement;
}

export const PdfTableRenderer = ({ section, table }: PdfTableRendererProps) => {
  const columnThElements =
    Array.from(
      table.querySelector('thead')
        ? table.querySelectorAll('thead tr:first-of-type:not(:has(td)) th')
        : table.querySelectorAll('tr:first-of-type:not(:has(td)) th'),
    ) ?? [];

  const rows =
    Array.from(table.querySelectorAll('tr:has(td)')).map((tr) =>
      Array.from(tr.querySelectorAll('td')).map((td) => td),
    ) ?? [];

  return (
    <View style={tableStyles.table}>
      {/* Header row */}
      {columnThElements.length > 0 && (
        <View style={[tableStyles.row, tableStyles.header]}>
          {columnThElements.map((header, index) => (
            <View
              key={uniqueIdentifierForNode({ section, node: header, context: 'table-header' })}
              style={
                index === columnThElements.length - 1 ? tableStyles.lastCell : tableStyles.cell
              }
            >
              <Text style={[tableStyles.header, styles.text]}>
                {trim(header.textContent ?? '')?.toUpperCase()}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Data rows */}
      {rows.map((row) => (
        <View
          key={uniqueIdentifierForNode({ section, node: row as never, context: 'table-row' })}
          style={tableStyles.row}
        >
          {row.map((cell, cellIndex) => (
            <View
              key={uniqueIdentifierForNode({ section, node: cell, context: 'table-cell' })}
              style={cellIndex === row.length - 1 ? tableStyles.lastCell : tableStyles.cell}
            >
              {Array.from(cell.childNodes).map((node) => {
                if (node.nodeName === 'th') {
                  return (
                    <Text style={[tableStyles.header, styles.text]}>
                      {trim(node.textContent ?? '')?.toUpperCase()}
                    </Text>
                  );
                }
                return (
                  <PdfNodeRenderer
                    key={uniqueIdentifierForNode({ section, node, context: 'table-cell-child' })}
                    section={section}
                    node={node}
                  />
                );
              })}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};
